import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { Icon } from '@/components/Icon'
import { formatDate, formatMoney } from '@/lib/format'
import { cardHref } from '@/lists/cellValue'
import { HistoryPanel } from './HistoryPanel'
import { deactivateSubscription, fetchSubscription, reactivateSubscription } from './invoiceApi'
import { Facts, Panel, RecordHeader, RecordTabs, type Fact, type HeaderAction, type MenuAction } from './record'
import {
  DateField,
  FailureAlert,
  FormModal,
  RecordFailure,
  RecordSkeleton,
  copyLinkItem,
  failureText,
  labelOf,
  todayIso,
} from './recordKit'
import type { RecordViewProps } from './types'

/** Путь списка подписок: экран, а не списочный пункт (ключ и ресурс права расходятся). */
export const SUBSCRIPTIONS_PATH = '/subscriptions'

/** Строка шаблона, как её хранит домен (`InvoiceService.LineRequest`). */
interface TemplateLine {
  description?: string | null
  quantity?: number | null
  unitPrice?: number | null
  discountPercent?: number | null
  taxRateCode?: string | null
}

/**
 * Карточка подписки на повторяющееся выставление (#94).
 *
 * Управление подпиской — здесь, а не кнопкой в таблице дебиторки: дебиторку открывают, чтобы
 * посмотреть долг, и «остановить» посреди неё останавливало бы выставление счетов из экрана,
 * который для этого не предназначен.
 *
 * - **Остановить** — счета перестают выставляться; подтверждение обязательно, потому что
 *   клиент перестаёт получать счета молча.
 * - **Возобновить** — с даты (по умолчанию сегодня). Пропущенные периоды **не догоняются**:
 *   сервер ставит ближайший будущий срок, и окно говорит это до нажатия, а не после.
 *   Подписку с прошедшей датой окончания сервер не возобновит, отказ показывается его словами.
 *
 * Право — `billing.invoice:update` (подписка своего права не имеет), и решает сервер по
 * области записи: кнопка у того, у кого нет права, скрыта только для удобства.
 *
 * Ближайшее списание показывается как есть, а не пересчитывается: его считает домен от
 * исходного дня месяца, и повтор расчёта здесь однажды показал бы 28-е вместо 31-го.
 *
 * ## Чего здесь нет
 *
 * Правки шаблона строк, суммы и расписания, внеочередного выставления (`/template`, `/bill`)
 * — эндпоинты есть, экрана нет; названо и в `invoiceApi`.
 */
export function SubscriptionRecordView({ recordId, record, context, notices }: RecordViewProps) {
  const { t, user, locale } = useApp()
  const queryClient = useQueryClient()
  const [tab, setTab] = useState('template')
  const [failure, setFailure] = useState<string | null>(null)
  const [dialog, setDialog] = useState<'stop' | 'resume' | null>(null)
  const [dialogFailure, setDialogFailure] = useState<string | null>(null)
  const [resumeOn, setResumeOn] = useState(todayIso())

  const subscription = useQuery({
    queryKey: ['subscription', recordId],
    queryFn: ({ signal }) => fetchSubscription(recordId, signal),
    retry: false,
  })

  const settle = {
    onSuccess: () => {
      setDialog(null)
      setDialogFailure(null)
      void queryClient.invalidateQueries({ queryKey: ['subscription', recordId] })
      void queryClient.invalidateQueries({ queryKey: ['record', 'billing.recurring', recordId] })
      void queryClient.invalidateQueries({ queryKey: ['list', 'billing.recurring'] })
      void queryClient.invalidateQueries({ queryKey: ['subscriptions'] })
    },
    onError: (error: unknown) => setDialogFailure(failureText(error, t('app.error'))),
  }
  const stop = useMutation({ mutationFn: () => deactivateSubscription(recordId), ...settle })
  const resume = useMutation({ mutationFn: () => reactivateSubscription(recordId, resumeOn || null), ...settle })

  if (subscription.isLoading) return <RecordSkeleton />
  if (!subscription.data) {
    return <RecordFailure error={subscription.error} onRetry={() => void subscription.refetch()} listPath={SUBSCRIPTIONS_PATH} />
  }
  const current = subscription.data
  const mayUpdate = can(user, 'billing.invoice', 'update')
  const busy = stop.isPending || resume.isPending
  const ended = current.endDate != null && current.endDate < todayIso()

  let primary: HeaderAction | null = null
  if (mayUpdate && current.active) {
    primary = { id: 'stop', label: t('record.sub.stop'), icon: 'close', onClick: () => setDialog('stop'), disabled: busy }
  } else if (mayUpdate && !current.active && !ended) {
    primary = { id: 'resume', label: t('record.sub.resume'), icon: 'refresh', onClick: () => setDialog('resume'), disabled: busy }
  }
  const menu: MenuAction[] = [copyLinkItem(t, setFailure)]

  const counterpartyName = labelOf(record.values, 'counterparty_id')
  const counterpartyHref = cardHref(user, 'sales.counterparty', current.counterpartyId)
  const contractName = labelOf(record.values, 'contract_id')
  const contractHref = current.contractId ? cardHref(user, 'billing.contract', current.contractId) : null

  const meta = [
    <span key="state" className={`flag ${current.active ? 'flag-success' : 'flag-muted'}`}>
      <Icon name={current.active ? 'check' : 'close'} />
      {current.active ? t('record.sub.active') : t('record.sub.stopped')}
    </span>,
    counterpartyName ? (
      <span key="cp" className="row" style={{ gap: 6 }}>
        <Icon name="building" size="sm" />
        {counterpartyHref ? <Link to={counterpartyHref}>{counterpartyName}</Link> : counterpartyName}
      </span>
    ) : null,
    contractName ? (
      <span key="contract">
        {t('record.sub.contract')}{' '}
        {contractHref ? <Link to={contractHref}>{contractName}</Link> : contractName}
      </span>
    ) : null,
  ]

  const facts: Fact[] = [
    {
      id: 'schedule',
      label: t('record.sub.schedule'),
      value: t(`recurring.frequency.${current.frequency}`),
      sub: t('record.sub.day', { day: current.dayOfMonth }),
    },
    {
      id: 'next',
      label: t('card.inv.sub.nextRun'),
      // A stopped subscription has no next charge: a date here would promise an invoice
      // that will not be issued.
      value: current.active && current.nextRunOn ? formatDate(current.nextRunOn, locale) : t('card.inv.sub.paused'),
    },
    {
      id: 'amount',
      label: t('column.amount'),
      value: current.amount == null ? '—' : formatMoney(current.amount, current.currency, locale),
    },
    {
      id: 'period',
      label: t('record.sub.period'),
      value: current.endDate
        ? `${formatDate(current.startDate, locale)} — ${formatDate(current.endDate, locale)}`
        : t('record.sub.since', { date: formatDate(current.startDate, locale) }),
      sub: ended ? t('record.sub.ended') : undefined,
    },
  ]

  const lines: TemplateLine[] = Array.isArray(current.template) ? (current.template as TemplateLine[]) : []

  return (
    <div className="rec-page">
      <RecordHeader
        crumbs={[{ label: t('nav.subscriptions'), to: SUBSCRIPTIONS_PATH }, { label: current.name }]}
        title={current.name}
        meta={meta}
        primary={primary}
        menu={menu}
        menuLabel={t('record.more')}
      />
      <FailureAlert text={failure} />
      {notices}
      <Facts items={facts} ariaLabel={t('record.facts')} />

      <RecordTabs
        tabs={[
          { id: 'template', label: t('record.sub.template'), count: lines.length },
          { id: 'history', label: t('record.tab.history') },
        ]}
        active={tab}
        onChange={setTab}
        ariaLabel={t('record.sub.sections')}
      >
        {tab === 'template' && (
          <div className="rec-col" style={{ paddingTop: 14 }}>
            <Panel title={t('record.sub.template')} flush>
              {lines.length === 0 ? (
                <p className="muted" style={{ padding: 12 }}>{t('record.sub.noLines')}</p>
              ) : (
                <table className="card-table" aria-label={t('record.sub.template')}>
                  <thead>
                    <tr>
                      <th>{t('card.inv.line.description')}</th>
                      <th>{t('card.inv.line.quantity')}</th>
                      <th>{t('card.inv.line.unitPrice')}</th>
                      <th>{t('card.inv.line.discount')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((line, index) => (
                      <tr key={index}>
                        <td>{line.description ?? '—'}</td>
                        <td>{line.quantity ?? '—'}</td>
                        <td>{line.unitPrice == null ? '—' : formatMoney(line.unitPrice, current.currency, locale)}</td>
                        <td>{line.discountPercent ? `${line.discountPercent}%` : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Panel>
          </div>
        )}
        {tab === 'history' && (
          <div className="rec-col" style={{ paddingTop: 14 }}>
            <Panel title={t('record.history.changes')}>
              <HistoryPanel entity={context.entity} recordId={recordId} />
            </Panel>
          </div>
        )}
      </RecordTabs>

      <FormModal
        open={dialog === 'stop'}
        danger
        icon="close"
        title={t('record.sub.stop.title', { name: current.name })}
        confirmLabel={t('record.sub.stop')}
        canConfirm
        pending={stop.isPending}
        failure={dialogFailure}
        onCancel={() => {
          setDialog(null)
          setDialogFailure(null)
        }}
        onConfirm={() => stop.mutate()}
      >
        <p className="secondary">{t('record.sub.stop.text')}</p>
      </FormModal>

      <FormModal
        open={dialog === 'resume'}
        icon="refresh"
        title={t('record.sub.resume.title', { name: current.name })}
        confirmLabel={t('record.sub.resume')}
        canConfirm={resumeOn !== ''}
        missingHint={t('record.sub.resume.missing')}
        pending={resume.isPending}
        failure={dialogFailure}
        onCancel={() => {
          setDialog(null)
          setDialogFailure(null)
        }}
        onConfirm={() => resume.mutate()}
      >
        <p className="secondary">{t('record.sub.resume.text')}</p>
        <DateField label={t('record.sub.resume.on')} value={resumeOn} onChange={setResumeOn} required />
      </FormModal>
    </div>
  )
}
